import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import { map, catchError, switchMap, withLatestFrom, tap } from "rxjs/operators";
import { MipsEligibilityActions } from "@store/actions/mips-eligibility/mips-eligibility.actions";
import { MipsEligibilityService } from "@services/mips-eligibility.service";
import { ErrorDialogService } from "@services/error-dialog.service";
import { selectActiveUser } from "@store/selectors/user/user.selectors";
import { MipsEligibilityResponse } from "@models/mips-stepper.model";

@Injectable()
export class MipsEligibilityEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly mipsEligibilityService = inject(MipsEligibilityService);
  private readonly errorDialogService = inject(ErrorDialogService);


  checkEligibility$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsEligibilityActions.checkEligibility),
      withLatestFrom(this.store.select(selectActiveUser)),
      switchMap(([{ npi, performanceYear, practice, tin }, activeUser]) => {
        return this.mipsEligibilityService
          .checkEligibility(npi, performanceYear, parseInt(practice), tin)
          .pipe(
            map((response) => {
              // Create the result with the array of organizations
              const result: MipsEligibilityResponse = {
                mipsEligibilityOptions: response.mipsEligibilityOptions,
                mvpRegistered: response.mvpRegistered,
                mvpId: response.mvpId,
              };

              // If there's only one organization, auto-select it
              if (response.mipsEligibilityOptions.length === 1) {
                const singleOrg = response.mipsEligibilityOptions[0];
                Object.assign(result, {
                  npi: singleOrg.npi,
                  clinicianName: singleOrg.clinicianName,
                  organizationName: singleOrg.organizationName,
                  individualEligible: singleOrg.individualEligible,
                  groupEligible: singleOrg.groupEligible,
                  subgroupEligible: singleOrg.subgroupEligible,
                  individualOptInEligible: singleOrg.individualOptInEligible,
                  groupOptInEligible: singleOrg.groupOptInEligible,
                  subgroupOptInEligible: singleOrg.subgroupOptInEligible,
                  performanceYear: singleOrg.performanceYear,
                  eligibilityReason: singleOrg.eligibilityReason,
                });
              }

              return MipsEligibilityActions.checkEligibilitySuccess({ result });
            }),
            catchError((error) => {
              let errorMessage =
                "Unable to check eligibility. Please verify the NPI and try again.";

              if (
                error.error?.errors &&
                typeof error.error.errors === "object"
              ) {
                const errorKeys = Object.keys(error.error.errors);

                if (errorKeys.length > 0) {
                  const firstErrorKey = errorKeys[0];
                  const errorArray = error.error.errors[firstErrorKey];

                  if (Array.isArray(errorArray) && errorArray.length > 0) {
                    errorMessage = errorArray[0];
                  } else {
                    errorMessage = firstErrorKey;
                  }
                }
              } else if (error.error?.title) {
                errorMessage = error.error.detail || error.error.title;
              } else if (error.error?.message) {
                errorMessage = error.error.message;
              } else if (error.message) {
                errorMessage = error.message;
              }

              return of(
                MipsEligibilityActions.checkEligibilityFailure({
                  error: errorMessage,
                })
              );
            })
          );
      })
    )
  );

  showEligibilityError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MipsEligibilityActions.checkEligibilityFailure),
        tap(({ error }) => {
          this.errorDialogService.showError(error, "Eligibility Check Failed");
        })
      ),
    { dispatch: false }
  );
}
